import React, { forwardRef } from 'react';
import Type from 'prop-types';
import noop from 'lodash/noop';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Portal,
} from '@mui/material';
import Link from '../Link';
import styles from './BottomNavMenu.module.css';

const BottomNavMenu = forwardRef(function BottomNavMenu(props, ref) {
  const { activeTab, tabs, onChange } = props;

  const handleChange = (event, newValue) => {
    onChange(newValue);
  };
  return (
    <Portal>
      <BottomNavigation
        ref={ref}
        value={activeTab}
        onChange={handleChange}
        showLabels
        sx={{
          position: 'fixed',
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1100,
          height: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          pb: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'border-box',
          boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.2)',
          '& .Mui-selected': {
            color: '#f7a707',
          },
          '& .Mui-selected::after': {
            content: '""',
            position: 'absolute',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '3px',
            backgroundColor: '#f7a707',
            borderRadius: '2px 2px 0 0',
          },
        }}
      >
        {tabs.map(
          ({
            value,
            label,
            icon: Icon,
            type,
            dataTestId,
            badge,
            badgeVariant,
            badgeColor,
          }) => {
            const linkProps =
              type === 'link' ? { component: Link, to: value } : {};

            return (
              <BottomNavigationAction
                key={value}
                label={label}
                value={value}
                icon={
                  badge ? (
                    <Badge
                      badgeContent={badgeVariant === 'dot' ? undefined : badge}
                      variant={badgeVariant || 'standard'}
                      color={badgeColor || 'error'}
                      invisible={!badge}
                      max={99}
                    >
                      <Icon />
                    </Badge>
                  ) : (
                    <Icon />
                  )
                }
                data-testid={dataTestId}
                classes={{ root: styles.button }}
                {...linkProps}
              />
            );
          },
        )}
      </BottomNavigation>
    </Portal>
  );
});

BottomNavMenu.propTypes = {
  activeTab: Type.oneOfType([Type.string.isRequired, Type.number.isRequired]),
  tabs: Type.arrayOf(
    Type.shape({
      type: Type.oneOf(['button', 'link', 'action']),
      label: Type.string,
      value: Type.string.isRequired,
      icon: Type.elementType,
      dataTestId: Type.string,
      badge: Type.oneOfType([Type.number, Type.bool]),
      badgeVariant: Type.oneOf(['standard', 'dot']),
      badgeColor: Type.string,
    }).isRequired,
  ).isRequired,
  onChange: Type.func,
};

BottomNavMenu.defaultProps = {
  onChange: noop,
  activeTab: null,
};

export default BottomNavMenu;
